import { AfterViewInit, Component, OnInit, signal } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { BrandingField } from '@app/core/models/branding.models';
import { AppFeature } from '@app/core/models/config.models';
import { AppConfigService } from '@app/core/services/app-config.service';
import { ProtocolHandlerService } from '@app/core/services/protocol-handler.service';
import { PwaService } from '@app/core/services/pwa.service';
import { ElectronService } from '@app/electron/electron.service';
import { LambdaLogDBModel } from '@app/preferences/models/lambda.model';
import { LambdaService } from '@app/preferences/services/lambda.service';
import { RendererIntegrationsService } from '@app/preferences/services/renderer-integrations.service';
import { RegisterTelHandlerDialogComponent } from '@app/shared/components/register-tel-handler-dialog/register-tel-handler-dialog.component';
import { downloadFile } from '@app/shared/utils/file.util';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Integration } from '@shared/models/integrations.models';
import { debounceTime, distinctUntilChanged, filter, firstValueFrom, map, merge } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'app-integrations',
  templateUrl: './integrations.component.html',
  styleUrls: ['./integrations.component.scss'],
})
export class IntegrationsComponent implements AfterViewInit, OnInit {
  protected readonly panelOpenState = signal(false);

  protected readonly isRunningInElectron = this.electronService.isRunningInElectron;
  protected readonly AppFeature = AppFeature;
  protected readonly BrandingField = BrandingField;
  protected autoPopDigitsInbound: FormControl;
  protected autoPopDigitsOutbound: FormControl;

  constructor(
    protected pwaService: PwaService,
    protected appConfigService: AppConfigService,
    protected protocolHandlerService: ProtocolHandlerService,
    protected electronService: ElectronService,
    protected integrationsService: RendererIntegrationsService,
    protected lambdaService: LambdaService,
    private dialog: MatDialog,
    private fb: FormBuilder
  ) {
    this.autoPopDigitsInbound = this.fb.control<number>(4, [Validators.min(1), Validators.max(15)]);
    this.autoPopDigitsOutbound = this.fb.control<number>(4, [Validators.min(1), Validators.max(15)]);
  }

  ngOnInit(): void {
    this.appConfigService.data$.pipe(untilDestroyed(this)).subscribe((config) => {
      this.autoPopDigitsInbound.setValue(config?.settings.preferences.webpop?.autoPopDigitsInbound ?? 4);
      this.autoPopDigitsOutbound.setValue(config?.settings.preferences.webpop?.autoPopDigitsOutbound ?? 4);
    });

    merge(
      this.autoPopDigitsInbound.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter((val) => this.isValidDigits(val)),
        map((val) => ({ type: 'inbound', val }))
      ),
      this.autoPopDigitsOutbound.valueChanges.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter((val) => this.isValidDigits(val)),
        map((val) => ({ type: 'outbound', val }))
      )
    ).subscribe(({ type, val }) => {
      if (type === 'inbound') {
        this.updateWebpopPreferences(val, this.autoPopDigitsOutbound.value);
      } else {
        this.updateWebpopPreferences(this.autoPopDigitsInbound.value, val);
      }
    });
  }

  private isValidDigits(num: number): boolean {
    return num >= 1 && num <= 15;
  }

  private async updateWebpopPreferences(inbound: number, outbound: number) {
    try {
      await firstValueFrom(
        this.appConfigService.updatePreferences({
          webpop: { autoPopDigitsInbound: inbound, autoPopDigitsOutbound: outbound },
        })
      );
    } catch (error) {
      console.error('Update webpop preferences failed', error);
    }
  }

  ngAfterViewInit(): void {
    this.refreshIntegrations();
  }

  async installConnectUCPwa() {
    if (await this.pwaService.promptInstall()) {
      this.dialog.open(RegisterTelHandlerDialogComponent, {
        data: {
          protocolUrl: ProtocolHandlerService.protocolUrl,
        },
        disableClose: true,
      });
    }
  }

  protected registerTelHandler() {
    try {
      this.protocolHandlerService.registerProtocolHandler('tel', ProtocolHandlerService.protocolUrl);
    } catch (error) {
      console.error('registerProtocolHandler error', error);
    }
  }

  protected async handleIntegrationUpdated(integration: Integration) {
    try {
      await this.integrationsService.updateIntegration(integration);
    } catch (error) {
      console.error('handleIntegrationUpdated error', error);
    }
  }

  protected handleDownloadLogClick(log: LambdaLogDBModel) {
    downloadFile(`lambda-log-${log.createdAt}.txt`, JSON.stringify(log, null, 2), 'document');
  }

  protected async refreshIntegrations() {
    try {
      await firstValueFrom(this.integrationsService.refreshData());
    } catch (error) {
      console.error('refreshIntegrations error', error);
    }
  }
}
